/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/form-control/Input';
import Button from '../../components/form-control/Button';
import Page from '../../components/PageWrapper';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import useUserAccount from '../../hooks/stateHooks/userAccountHook';
import { activateUser } from '../../api/requests';
import { IUser } from '../../components/models/User';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

interface IProps {
  onActivated: (iUser: IUser) => void;
}

function ActivateAccount(props: IProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { updateAccount, user, updateProfile } = useUserAccount();

  const userEmail = searchParams.get('ue');

  const submitHandler = (values: any) => {
    setSubmitting(true);
    const payload: any = {
      password: values.password
    };

    activateUser(payload)
      .then((response: any) => {
        props.onActivated(response.data);
        // updateProfile(response.data);
      })
      .catch((error: any | AxiosError) => {
        toast.error(`${error?.detail}.`, {
          autoClose: 5000
        });
      }).finally(() => setSubmitting(false));
  };

  return (
    <Page title="Activate account">
      <Formik
        initialValues={{
          password: '',
          confirmPassword: '',
        }}
        validateOnChange={false}
        validateOnBlur={true}
        validationSchema={Yup.object({
          password: Yup.string()
            .trim()
            .required('Password is required')
            .min(8, 'A minimum of 9 characters required')
            .test({
              message: 'User email cannot be used as password',
              test: (value) => value !== userEmail
            })
            .matches(
              /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/,
              'Password must include at least two of the following: an uppercase character, a number, a special character'
            ),
          confirmPassword: Yup.string()
            .required('Password confirmation is required')
            // @ts-ignore
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
        })}
        onSubmit={(values) => {
          submitHandler(values);
        }}>
        {({ values, errors, handleSubmit, handleChange, handleBlur, setFieldValue, touched }) => (
          <form
            className="px-2 py-4 md:px-8 my-7 bg-white rounded-[8px] md:w-[500px]"
            onSubmit={handleSubmit}>
            <div className="mb-5">
              <p className="text-lg text-center font-semibold">
                Welcome {user.firstName}, choose your own password to activate your account.
              </p>
            </div>
            <div className="">

              <Input
                name="email"
                label="Email"
                value={user.email}
                type="email"
                wrapperClasses="my-5"
                error={touched.password ? errors.password : undefined}
                onBlur={handleBlur}
                onChange={handleChange}
                disabled
                append={<CheckCircleIcon className="w-7 text-[#00c07b] mr-2" />}
              />

              <Input
                name="password"
                label="Password"
                value={values.password}
                type="password"
                wrapperClasses="my-5"
                showPasswordIcon
                required
                error={touched.password ? errors.password : undefined}
                onBlur={handleBlur}
                onChange={handleChange}
                helperText={touched.password ? errors.password : undefined}
                description={`Must be more than 8 characters. Include an uppercase character, a number and a special character`}
              />
              {errors.password && (
                <div className="flex flex-col font-bold text-gray-500 text-sm">
                  <div className="">
                    <span> Password must:</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 bg-gray-500 rounded-full  "></div>{' '}
                      <span>Be between 9 and 64 characters</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 bg-gray-500 rounded-full  "></div>{' '}
                      <span>Includes at least two of the following:</span>
                    </div>
                  </div>

                  <div className="ml-5 flex flex-col">
                    <span>&#x2022; An uppercase character</span>
                    <span>&#x2022; A number</span>
                    <span>&#x2022; A special character</span>
                  </div>
                </div>
              )}

              <Input
                name="confirmPassword"
                label="Confirm Password"
                value={values.confirmPassword}
                type="password"
                wrapperClasses="my-5"
                showPasswordIcon
                required
                error={touched.confirmPassword ? errors.confirmPassword : undefined}
                onBlur={handleBlur}
                onChange={handleChange}
                helperText={touched.confirmPassword ? errors.confirmPassword : undefined}
              />

              <Button
                type="submit"
                label="Activate account"
                bgColor="primary"
                className="mt-5 w-full rounded-[6px] !bg-dark border-[1px] border-dark !text-white"
                loading={submitting}
                disabled={submitting}
              />
            </div>
          </form>
        )}
      </Formik>
    </Page>
  );
}

export default ActivateAccount;
